import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { fetchUserCfpSessionIsExisting, createCfpSessionCheckout } from '../../../utils/api';
import styles from './ScheduleCfpSessionPage.module.css';

function CheckIcon() {
  return (
    <svg className={styles.footerCheckIcon} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M5 8.2L7.1 10.3L11.5 5.8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ConfirmArrowIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M4 10H16M16 10L11 5M16 10L11 15"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CfpSessionFooter({
  selectedCount,
  selectedTopic,
  expertId,
  isSubmitting = false,
  navigate,
  onConfirm,
}) {
  const isReady = selectedCount > 0;
  const [isChecking, setIsChecking] = useState(false);
  const isBusy = isSubmitting || isChecking;
  const selectedTopicTitle = selectedTopic?.topic ?? null;

  const handleConfirmClick = useCallback(async () => {
    if (!isReady || isBusy || !selectedTopic) return;

    setIsChecking(true);
    const existing = await fetchUserCfpSessionIsExisting(navigate);

    // After Initial session is done (or any session already exists), use paid checkout
    if (existing?.mustUseCheckout) {
      const checkout = await createCfpSessionCheckout(
        {
          tabLabel: selectedTopic.tabLabel,
          topicCardTitle: selectedTopic.topicCardTitle,
          expertId,
        },
        navigate,
      );
      setIsChecking(false);

      if (checkout.ok && checkout.checkoutUrl) {
        window.location.href = checkout.checkoutUrl;
        return;
      }

      alert(checkout.message || 'Unable to start checkout. Please try again.');
      return;
    }

    setIsChecking(false);
    onConfirm();
  }, [isReady, isBusy, selectedTopic, expertId, navigate, onConfirm]);

  return (
    <footer className={styles.footerBar}>
      <div className={styles.footerInner}>
        <div className={styles.footerLeft}>
          <AnimatePresence initial={false}>
            {selectedTopicTitle && (
              <motion.div
                key="topic-title"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
                className={styles.footerSelectedTopic}
              >
                <span className={styles.footerSelectedTopicLabel}>Selected:</span>
                <span className={styles.footerSelectedTopicTitle}>{selectedTopicTitle}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence mode="wait" initial={false}>
            {isReady ? (
              <motion.div
                key="ready"
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                className={styles.footerHelper}
              >
                <CheckIcon />
                <span>Ready for your session</span>
              </motion.div>
            ) : (
              <motion.div
                key="prompt"
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                className={styles.footerHelper}
              >
                <span>Select 1 topic to personalize your session</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className={styles.footerActions}>
          <motion.button
            type="button"
            className={`${styles.footerConfirmButton} ${isReady ? styles.footerConfirmButtonReady : ''}`}
            onClick={handleConfirmClick}
            disabled={!isReady || isBusy}
            whileHover={!isReady || isBusy ? {} : { scale: 1.03, y: -1 }}
            whileTap={!isReady || isBusy ? {} : { scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 420, damping: 26 }}
          >
            <span>
              {isChecking ? 'Redirecting to checkout…' : isSubmitting ? 'Saving…' : 'Confirm Selection'}
            </span>
            <motion.span
              className={styles.footerConfirmArrow}
              aria-hidden="true"
              animate={isReady ? { x: [0, 3, 0] } : {}}
              transition={{
                repeat: Infinity,
                duration: 1.6,
                ease: 'easeInOut',
                repeatDelay: 0.8,
              }}
            >
              <ConfirmArrowIcon />
            </motion.span>
          </motion.button>
        </div>
      </div>
    </footer>
  );
}

export default CfpSessionFooter;
